import { EventEmitter, Inject, Injectable } from '@angular/core';

import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { debounceTime } from 'rxjs';

// devextreme localization
import { locale, loadMessages } from 'devextreme/localization';
import trMessages from 'devextreme/localization/messages/tr.json';
import enMessages from 'devextreme/localization/messages/en.json';
import itMessages from 'devextreme/localization/messages/it.json';
import deMessages from 'devextreme/localization/messages/de.json';
import frMessages from 'devextreme/localization/messages/fr.json';
import nlMessages from 'devextreme/localization/messages/nl.json';
import arMessages from 'devextreme/localization/messages/ar.json';
import uzMessages from '../locales/devextreme/uz.json'; // Uzbek
import ugMessages from '../locales/devextreme/ug.json'; // Uyghur
import ukMessages from '../locales/devextreme/uk.json'; // Ukrainian
import tkMessages from '../locales/devextreme/tk.json'; // Turkmen
import ttMessages from '../locales/devextreme/tt.json'; // Tatar
import svMessages from 'devextreme/localization/messages/sv.json';
// import soMessages from 'devextreme/localization/messages/so.json';
// import srMessages from 'devextreme/localization/messages/sr.json';
import ruMessages from 'devextreme/localization/messages/ru.json';
import roMessages from 'devextreme/localization/messages/ro.json';
import ptMessages from 'devextreme/localization/messages/pt.json';
import plMessages from 'devextreme/localization/messages/pl.json';
import faMessages from 'devextreme/localization/messages/fa.json';
// import noMessages from 'devextreme/localization/messages/no.json';
// import kyMessages from 'devextreme/localization/messages/ky.json';
// import koMessages from 'devextreme/localization/messages/ko.json';
// import kkMessages from 'devextreme/localization/messages/kk.json';
import esMessages from 'devextreme/localization/messages/es.json';
import jaMessages from 'devextreme/localization/messages/ja.json';
import huMessages from 'devextreme/localization/messages/hu.json';
// import hiMessages from 'devextreme/localization/messages/hi.json';
import elMessages from 'devextreme/localization/messages/el.json';
import kaMessages from '../locales/devextreme/ka.json'; // Georgian
import fiMessages from 'devextreme/localization/messages/fi.json';
import csMessages from 'devextreme/localization/messages/cs.json';
// import zh-TWMessages from 'devextreme/localization/messages/zh-TW.json';
// import zh-CNMessages from 'devextreme/localization/messages/zh-CN.json';
// import azMessages from 'devextreme/localization/messages/az.json';
import { BaseService } from '@app/core/services/base-service.service';
import { Language } from './models/languages';

import { DOCUMENT } from '@angular/common';
import { LOCALE_ID } from '@angular/core';

import dayjs from '@shared/date/dayjs-bootstrap';
import { ServiceName, ServiceReadyStateService } from '@app/core/services/service-ready-state.service';

@Injectable({
    providedIn: 'root'
})
export class LanguageService extends BaseService {


    supportedLanguages: Language[] = [
        {
            code: 'tr-TR',
            title: 'Türkçe',
            flag: 'assets/images/flags/TR.svg',
            key: 'tr',
            direction: 'ltr',
        },
        {
            code: 'en-US',
            title: 'English',
            flag: 'assets/images/flags/US.svg',
            key: 'en',
            direction: 'ltr',
        },
        {
            code: 'separator',
            title: '',
            flag: '',
            key: '',
            direction: '',
        },
        {
            code: 'de-DE',
            title: 'Deutsch',
            flag: 'assets/images/flags/DE.svg',
            key: 'de',
            direction: 'ltr',
        },
        {
            code: 'fr-FR',
            title: 'Français',
            flag: 'assets/images/flags/FR.svg',
            key: 'fr',
            direction: 'ltr',
        },
        {
            code: 'it-IT',
            title: 'Italiano',
            flag: 'assets/images/flags/IT.svg',
            key: 'it',
            direction: 'ltr',
        },
        {
            code: 'nl-NL',
            title: 'Nederlands',
            flag: 'assets/images/flags/NL.svg',
            key: 'nl',
            direction: 'ltr'
        },
        {
            code: 'ar-SA',
            title: 'العربية',
            flag: 'assets/images/flags/SA.svg',
            key: 'ar',
            direction: 'ltr',
        },
        {
            code: 'uz-UZ',
            title: 'Uzbek',
            flag: 'assets/images/flags/UZ.svg',
            key: 'uz',
            direction: 'ltr',
        },
        {
            code: 'ug-CN',
            title: 'ئۇيغۇرچە',
            flag: 'assets/images/flags/UY.svg',
            key: 'ug',
            direction: 'ltr',
        },
        {
            code: 'uk-UA',
            title: 'Українська',
            flag: 'assets/images/flags/UA.svg',
            key: 'uk',
            direction: 'ltr',
        },
        {
            code: 'tk-TM',
            title: 'Türkmençe',
            flag: 'assets/images/flags/TM.svg',
            key: 'tk',
            direction: 'ltr',
        },
        {
            code: 'tt-TT',
            title: 'Tatarça',
            flag: 'assets/images/flags/TATAR.svg',
            key: 'tt',
            direction: 'ltr',
        },
        {
            code: 'sv-SE',
            title: 'Svenska',
            flag: 'assets/images/flags/SE.svg',
            key: 'sv',
            direction: 'ltr',
        },
        {
            code: 'es-ES',
            title: 'Español',
            flag: 'assets/images/flags/ES.svg',
            key: 'es',
            direction: 'ltr',
        },
        {
            code: 'so-SO',
            title: 'Soomaali',
            flag: 'assets/images/flags/SO.svg',
            key: 'so',
            direction: 'ltr'
        },
        {
            code: 'sr-SR',
            title: 'Српски',
            flag: 'assets/images/flags/RS.svg',
            key: 'sr',
            direction: 'ltr',
        },
        {
            code: 'ru-RU',
            title: 'Русский',
            flag: 'assets/images/flags/RU.svg',
            key: 'ru',
            direction: 'ltr',
        },
        {
            code: 'ro-RO',
            title: 'Română',
            flag: 'assets/images/flags/RO.svg',
            key: 'ro',
            direction: 'ltr',
        },
        {
            code: 'pt-PT',
            title: 'Português',
            flag: 'assets/images/flags/PT.svg',
            key: 'pt',
            direction: 'ltr',
        },
        {
            code: 'pl-PL',
            title: 'Polski',
            flag: 'assets/images/flags/PL.svg',
            key: 'pl',
            direction: 'ltr',
        },
        {
            code: 'fa-FA',
            title: 'فارسی',
            flag: 'assets/images/flags/IR.svg',
            key: 'fa',
            direction: 'ltr',
        },
        {
            code: 'no-NO',
            title: 'Norsk',
            flag: 'assets/images/flags/NO.svg',
            key: 'no',
            direction: 'ltr',
        },
        {
            code: 'ky-KY',
            title: 'Кыргызча',
            flag: 'assets/images/flags/KG.svg',
            key: 'ky',
            direction: 'ltr',
        },
        {
            code: 'ko-KO',
            title: '한국어',
            flag: 'assets/images/flags/KR.svg',
            key: 'ko',
            direction: 'ltr',
        },
        {
            code: 'kk-KK',
            title: 'Қазақша',
            flag: 'assets/images/flags/KZ.svg',
            key: 'kk',
            direction: 'ltr',
        },
        {
            code: 'ja-JA',
            title: '日本語',
            flag: 'assets/images/flags/JP.svg',
            key: 'ja',
            direction: 'ltr',
        },
        {
            code: 'hu-HU',
            title: 'Magyar',
            flag: 'assets/images/flags/HU.svg',
            key: 'hu',
            direction: 'ltr',
        },
        {
            code: 'hi-HI',
            title: 'हिन्दी',
            flag: 'assets/images/flags/IN.svg',
            key: 'hi',
            direction: 'ltr',
        },
        {
            code: 'el-EL',
            title: 'Ελληνικά',
            flag: 'assets/images/flags/GR.svg',
            key: 'el',
            direction: 'ltr',
        },
        {
            code: 'ka-KA',
            title: 'ქართული',
            flag: 'assets/images/flags/GE.svg',
            key: 'ka',
            direction: 'ltr',
        },
        {
            code: 'fi-FI',
            title: 'Suomi',
            flag: 'assets/images/flags/FI.svg',
            key: 'fi',
            direction: 'ltr',
        },
        {
            code: 'cs-CS',
            title: 'Čeština',
            flag: 'assets/images/flags/CZ.svg',
            key: 'cs',
            direction: 'ltr',
        },
        {
            code: 'zh-TW-ZH-TW',
            title: '繁體中文',
            flag: 'assets/images/flags/TW.svg',
            key: 'zh-TW',
            direction: 'ltr',
        },
        {
            code: 'zh-CN-ZH-CN',
            title: '简体中文',
            flag: 'assets/images/flags/CN.svg',
            key: 'zh-CN',
            direction: 'ltr',
        },
        {
            code: 'az-AZ',
            title: 'Azərbaycanca',
            flag: 'assets/images/flags/AZ.svg',
            key: 'az',
            direction: 'ltr',
        },
        {
            code: 'bg-BG',
            title: 'Български',
            flag: 'assets/images/flags/BG.svg',
            key: 'bg',
            direction: 'ltr'
        },
        {
            code: 'af-ZA',
            title: 'Afrikaans',
            flag: 'assets/images/flags/ZA.svg',
            key: 'af',
            direction: 'ltr'
        }
    ];

    languageChangedAndReady: EventEmitter<Language> = new EventEmitter<Language>(); // create an EventEmitter

    browserCulture: string;
    availableLanguages: Language[];
    _selectedLanguage: Language;

    set selectedLanguage(value: Language) {
        if (!value) {
            return;
        }



        // ngx-translate localization
        this._selectedLanguage = value;
        this.translateService.use(value.key);
        this.translateService.setDefaultLang(value.key);
        localStorage.setItem('language', value.key);



        // dayjs localization
        dayjs.locale(this.translateService.currentLang) // use locale


        // devextreme localization
        locale(this.translateService.currentLang);

        switch (this.translateService.currentLang) {
            case 'tr':
                loadMessages(trMessages);
                break;
            case 'it':
                loadMessages(itMessages);
                break;
            case 'de':
                loadMessages(deMessages);
                break;
            case 'fr':
                loadMessages(frMessages);
                break;
            case 'es':
                loadMessages(esMessages);
                break;
            case 'nl':
                loadMessages(nlMessages);
                break;
            case 'ar':
                loadMessages(arMessages);
                break;
            case 'uz':
                loadMessages(uzMessages);
                break;
            case 'ug':
                loadMessages(ugMessages);
                break;
            case 'uk':
                loadMessages(ukMessages);
                break;
            case 'tk':
                loadMessages(tkMessages);
                break;
            case 'tt':
                loadMessages(ttMessages);
                break;
            case 'sv':
                loadMessages(svMessages);
                break;
            case 'so':
                loadMessages(enMessages);
                break;
            case 'sr':
                loadMessages(enMessages);
                break;
            case 'ru':
                loadMessages(ruMessages);
                break;
            case 'ro':
                loadMessages(roMessages);
                break;
            case 'pt':
                loadMessages(ptMessages);
                break;
            case 'pl':
                loadMessages(plMessages);
            // break;
            case 'fa':
                loadMessages(faMessages);
                break;
            case 'no':
                loadMessages(enMessages);
                break;
            case 'ky':
                loadMessages(enMessages);
                break;
            case 'ko':
                loadMessages(enMessages);
                break;
            case 'kk':
                loadMessages(enMessages);
                break;
            case 'ja':
                loadMessages(jaMessages);
                break;
            case 'hu':
                loadMessages(huMessages);
                break;
            case 'hi':
                loadMessages(enMessages);
                break;
            case 'el':
                loadMessages(elMessages);
                break;
            case 'ka':
                loadMessages(kaMessages);
                break;
            case 'fi':
                loadMessages(fiMessages);
                break;
            case 'cs':
                loadMessages(csMessages);
                break;
            case 'zh-TW':
                loadMessages(enMessages);
                break;
            case 'zh-CN':
                loadMessages(enMessages);
                break;
            case 'az':
                loadMessages(enMessages);
                break;
            case 'bg':
                loadMessages(enMessages);
                break;
            case 'af':
                loadMessages(enMessages);
                break;
            case 'en':
            default:
                loadMessages(enMessages);
                break;
        }

        this.languageChangedAndReady.emit(value);

        this.readyStateService.setServiceReady(ServiceName.Language, true);

    }

    constructor(public translateService: TranslateService,
        private readyStateService: ServiceReadyStateService
    ) {
        super();
    }

    init(): void {

        this.browserCulture = this.getBrowserCulture();
        this.availableLanguages = this.supportedLanguages.toArray();
        const lang = localStorage.getItem('language');
        this.setLanguage(lang);
    }

    get selectedLanguage(): Language {

        return this._selectedLanguage;
    }


    setLanguage(languageCode: string): void {
        this.selectedLanguage = this.availableLanguages.firstOrDefault(x => x.code.startsWith(languageCode));
        if (this.selectedLanguage) { return; }

        this.selectedLanguage = this.availableLanguages.firstOrDefault(x => x.code.startsWith(this.browserCulture));
        if (this.selectedLanguage) { return; }

        this.selectedLanguage = this.availableLanguages.firstOrDefault(x => x.code === 'en-US');
    }

    moduleInit(service: TranslateService, name: string): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.log.debug('LanguageService -> moduleInit -> name', name);

            this.refresh(service);

            const langChangeSubscription = service.onDefaultLangChange.pipe(
                debounceTime(1000)
            ).subscribe({
                next: (event: LangChangeEvent) => {
                    this.log.debug('LanguageService -> moduleInit -> event', name);
                    this.refresh(service);
                },
                complete: () => {
                    resolve();
                    langChangeSubscription.unsubscribe();
                },
                error: (err) => {
                    reject(err);
                    langChangeSubscription.unsubscribe();
                }
            });
        });
    }

    refresh(service: TranslateService): void {
        const exchange = service.currentLang;
        service.currentLang = "";
        service.use(exchange);

        this.languageChangedAndReady.emit(this.selectedLanguage);
    }


    /**
     * Gets the culture of the user's browser.
     * @returns The culture code of the user's browser, or null if it cannot be determined.
     */
    getBrowserCulture(): string {
        const nav = window.navigator;
        // Support for HTML 5.1 "navigator.languages"
        if (Array.isArray(nav.languages)) {
            const foundLanguage = nav.languages.find(lang => lang && lang.length);
            if (foundLanguage) return foundLanguage;
        }

        // Support for other well-known properties in browsers
        const browserLanguagePropertyKeys = ['language', 'browserLanguage', 'systemLanguage', 'userLanguage'];
        const foundKey = browserLanguagePropertyKeys.find(key => nav[key] && nav[key].length);
        return foundKey ? nav[foundKey] : null;
    }


}
